'use client';

import type {SxProps, Theme} from '@mui/material/styles';
import {styled} from '@mui/material/styles';
import {forwardRef, type HTMLAttributes, type ReactNode} from 'react';
import {Typography} from './Typography';

export type ChipType = 'filled' | 'outline';
export type ChipColor = 'dark' | 'light' | 'brand';
export type ChipSize = 'small' | 'medium';

export interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  type?: ChipType;
  color?: ChipColor;
  size?: ChipSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

interface ChipTokens {
  background?: string;
  border?: string;
  text: string;
  icon: string;
}

function chipTokens(theme: Theme, type: ChipType, color: ChipColor, size: ChipSize): ChipTokens {
  const {surface, text, border, icon} = theme.palette.colors;
  const tokens: Record<ChipType, Record<ChipColor, ChipTokens>> = {
    filled: {
      dark: {
        background: surface.label,
        text: text.neutral.inverse,
        icon: size === 'small' ? icon.neutral.inverse : icon.primary.default,
      },
      light: {background: surface.neutral.default, text: text.neutral.strong, icon: icon.neutral.strong},
      brand: {
        background: size === 'small' ? text.primary.default : surface.primary.default,
        text: text.neutral.inverse,
        icon: icon.neutral.inverse,
      },
    },
    outline: {
      dark: {border: border.neutral.strong, text: text.neutral.strong, icon: icon.primary.default},
      light: {border: border.neutral.inverseStrong, text: text.neutral.inverse, icon: icon.neutral.inverse},
      brand: {border: border.primary.default, text: text.primary.default, icon: text.primary.default},
    },
  };
  return tokens[type][color];
}

const SIZE_STYLES = {
  small: {height: 27, radius: 'sm', fontSize: 'body-3', fontWeight: 'regular', letterSpacing: 0},
  medium: {height: 32, radius: 'md', fontSize: 'caption', fontWeight: 'medium', letterSpacing: '0.02em'},
} as const;

const ChipRoot = styled('span', {
  shouldForwardProp: propName => !['oType', 'oColor', 'oSize', 'oIconLeft', 'oIconRight', 'sx'].includes(propName as string),
})<{oType: ChipType; oColor: ChipColor; oSize: ChipSize; oIconLeft: boolean; oIconRight: boolean}>(({
  theme,
  oType,
  oColor,
  oSize,
  oIconLeft,
  oIconRight,
}) => {
  const tokens = chipTokens(theme, oType, oColor, oSize);
  const size = SIZE_STYLES[oSize];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    boxSizing: 'border-box',
    height: size.height,
    paddingInlineStart: theme.spacing(oIconLeft ? 1.25 : 1.5),
    paddingInlineEnd: theme.spacing(oIconRight ? 1.25 : 1.5),
    borderRadius: theme.radii[size.radius],
    border: `1px solid ${tokens.border ?? 'transparent'}`,
    backgroundColor: tokens.background ?? 'transparent',
    color: tokens.text,
    whiteSpace: 'nowrap',
    '& svg': {fontSize: 16, color: tokens.icon, flexShrink: 0},
  };
});

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({type = 'filled', color = 'dark', size = 'small', iconLeft, iconRight, children, ...rest}, ref) => (
    <ChipRoot ref={ref} oType={type} oColor={color} oSize={size} oIconLeft={!!iconLeft} oIconRight={!!iconRight} {...rest}>
      {iconLeft}
      <Typography
        as="span"
        family="primary"
        size={SIZE_STYLES[size].fontSize}
        weight={SIZE_STYLES[size].fontWeight}
        sx={{color: 'inherit', letterSpacing: SIZE_STYLES[size].letterSpacing, textBox: 'trim-both cap alphabetic'}}
      >
        {children}
      </Typography>
      {iconRight}
    </ChipRoot>
  ),
);

Chip.displayName = 'Chip';
